import React, { useState, useEffect, useRef } from 'react';
import './ElementSettingsPopup.css';
import RichTextEditor from '../RichTextEditor/RichTextEditor';

function ElementSettingsPopup({ 
  isOpen, 
  element, 
  onClose, 
  updateElementText,
  setShowAssetsSidebar,
  cmsContentTypes = []
}) {
  const [popupSettings, setPopupSettings] = useState({
    text: '',
    contentTypeId: ''
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const popupRef = useRef(null);

  // Update popup settings when element changes
  useEffect(() => {
    if (element) {
      setPopupSettings({
        text: element.text || '',
        contentTypeId: element.contentTypeId || ''
      });
    }
  }, [element]);

  // Set initial position when popup opens
  useEffect(() => {
    if (isOpen && popupRef.current) {
      // Center the popup on screen
      const rect = popupRef.current.getBoundingClientRect();
      setPosition({
        x: (window.innerWidth - rect.width) / 2,
        y: Math.max(50, (window.innerHeight - rect.height) / 3)
      });
    }
  }, [isOpen]);

  // Add global mouse event handlers when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Move the event handlers outside the render function but define them before they're used
  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!isOpen || !element) return null;

  // Handle popup changes
  const handlePopupChange = (property, value) => {
    // Update local state
    setPopupSettings(prev => ({
      ...prev,
      [property]: value
    }));
    
    try {
      if (property === 'text') {
        // Properly construct the updated element with all original properties intact
        const updatedElement = { ...element };
        
        // Update just the text property
        updatedElement.text = value;
        
        // Call updateElementText with the element ID and the complete updated element
        updateElementText(element.id, updatedElement);
      }
    } catch (error) {
      console.error('Error updating element text:', error);
    }
  };
  
  // Rich text editor change handler
  const handleRichTextChange = (html) => {
    handlePopupChange('text', html);
  };
  
  // Open full settings panel
  const handleShowAllSettings = () => {
    // Close this popup
    onClose();
    
    // Dispatch a custom event to show element settings in the sidebar
    document.dispatchEvent(new CustomEvent('showElementInspectorSettings', { 
      detail: { elementId: element.id }
    }));
  };

  // Handle opening the asset sidebar for image replacement
  const handleReplaceImage = () => {
    // Close the popup
    onClose();
    
    // Open the asset sidebar
    setShowAssetsSidebar(true);

    // Dispatch a custom event to show element settings in the sidebar
    document.dispatchEvent(new CustomEvent('showElementInspectorSettings', { 
      detail: { elementId: element.id }
    }));
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.popup-header-drag')) {
      setIsDragging(true);
      const rect = popupRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    }
  };

  // Add rich-text check
  const isRichText = element.type === 'rich-text';
  const isHeading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(element.type);
  const isButton = element.type === 'button';
  const isImage = element.type === 'image';
  const isCMSContent = element.type === 'cms-content';
  const isCartElement = ['cart', 'cart-button', 'cart-wrapper', 'add-to-cart'].includes(element.type);
  const isTextElement = isHeading || isButton || element.type === 'p' || element.type === 'span' || element.type === 'a';
  
  // Get image URL for preview
  const getImageUrl = () => {
    if (!isImage) return '';
    
    // Check for src property first
    if (element.src) return element.src;
    
    // Then check for backgroundImage in styles
    if (element.styles?.backgroundImage) {
      return element.styles.backgroundImage
        .replace('url(', '')
        .replace(')', '')
        .replace(/"/g, '');
    }
    
    // Default placeholder
    return 'https://placehold.co/150x150';
  };
  
  // Render different settings based on element type
  const popupStyle = {
    left: `${position.x}px`,
    top: `${position.y}px`,
    position: 'fixed',
    cursor: isDragging ? 'grabbing' : 'auto'
  };

  // Image Element UI
  if (isImage) {
    const imageUrl = getImageUrl();
    
    return (
      <div 
        className="element-settings-popup" 
        style={popupStyle}
        onClick={e => e.stopPropagation()}
        ref={popupRef}
      >
        <div 
          className="popup-header-settings popup-header-drag"
          onMouseDown={handleMouseDown}
        >
          <h4>IMAGE Settings</h4>
          <button 
            className="popup-close-btn" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="popup-setting-group">
          <div className="image-preview-horizontal">
            <div className="image-container">
              <img 
                src={imageUrl} 
                alt="Selected Image" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100px',
                  display: 'block',
                  border: '1px solid rgba(0, 102, 255, 0.2)',
                  borderRadius: '4px'
                }} 
              />
            </div>
            <div className="image-actions">
              <button 
                className="image-replace-btn"
                onClick={handleReplaceImage}
              >
                Replace Image
              </button>
            </div>
          </div>
        </div>
        
        <div className="popup-actions">
          <button 
            className="show-all-settings" 
            onClick={handleShowAllSettings}
          >
            Show All Settings
          </button>
        </div>
      </div>
    );
  }

  // Text Element UI
  if (isTextElement) {
    // Create a dynamic title based on element type
    const elementTitle = isHeading ? element.type.toUpperCase() : 
                         isButton ? 'BUTTON' : 
                         element.type === 'p' ? 'PARAGRAPH' :
                         element.type === 'a' ? 'LINK' : 'TEXT';
    
    return (
      <div 
        className="element-settings-popup" 
        style={popupStyle}
        onClick={e => e.stopPropagation()}
        ref={popupRef}
      >
        <div 
          className="popup-header-settings popup-header-drag"
          onMouseDown={handleMouseDown}
        >
          <h4>{elementTitle} Settings</h4>
          <button 
            className="popup-close-btn" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="popup-setting-group">
          <label>Text</label>
          <input 
            type="text" 
            value={popupSettings.text} 
            onChange={e => handlePopupChange('text', e.target.value)}
            placeholder={`Enter ${elementTitle.toLowerCase()} text`}
            autoFocus
          />
        </div>
        
        <div className="popup-actions">
          <button 
            className="show-all-settings" 
            onClick={handleShowAllSettings}
          >
            Show All Settings
          </button>
        </div>
      </div>
    );
  }
  
  // CMS Content Element UI
  if (isCMSContent) {
    const handleContentTypeChange = (e) => {
      const newContentTypeId = e.target.value;
      
      // Update local state
      setPopupSettings(prev => ({
        ...prev,
        contentTypeId: newContentTypeId
      }));
      
      try {
        // Properly construct the updated element with all original properties intact
        const updatedElement = { 
          ...element, 
          contentTypeId: newContentTypeId,
          // Preserve existing display settings if they exist
          displayType: element.displayType || 'list',
          limit: element.limit || 10,
          sortBy: element.sortBy || 'createdAt',
          sortOrder: element.sortOrder || 'desc'
        };
        
        // Call updateElementText with the element ID and the complete updated element
        updateElementText(element.id, updatedElement);
      } catch (error) {
        console.error('Error updating content type:', error);
      }
    };

    return (
      <div 
        className="element-settings-popup" 
        style={popupStyle}
        onClick={e => e.stopPropagation()}
        ref={popupRef}
      >
        <div 
          className="popup-header-settings popup-header-drag"
          onMouseDown={handleMouseDown}
        >
          <h4>CMS CONTENT Settings</h4>
          <button 
            className="popup-close-btn" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="popup-setting-group">
          <label>Content Type</label>
          <select 
            value={popupSettings.contentTypeId} 
            onChange={handleContentTypeChange}
            className="content-type-select"
          >
            <option value="">Select Content Type</option>
            {cmsContentTypes.map(type => (
              <option key={type._id} value={type._id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="popup-actions">
          <button 
            className="show-all-settings" 
            onClick={handleShowAllSettings}
          >
            Show All Settings
          </button>
        </div>
      </div>
    );
  }
  
  // Cart Element UI - simple popup with just the "Show All Settings" button
  if (isCartElement) {
    const cartElementTitle = element.type.toUpperCase().replace('-', ' ');
    
    return (
      <div 
        className="element-settings-popup" 
        style={popupStyle}
        onClick={e => e.stopPropagation()}
        ref={popupRef}
      >
        <div 
          className="popup-header-settings popup-header-drag"
          onMouseDown={handleMouseDown}
        >
          <h4>{cartElementTitle} Settings</h4>
          <button 
            className="popup-close-btn" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="popup-setting-group">
          <p className="cart-settings-note">
            Cart settings are available in the inspector panel.
          </p>
        </div>
        
        <div className="popup-actions">
          <button 
            className="show-all-settings" 
            onClick={handleShowAllSettings}
          >
            Show All Settings
          </button>
        </div>
      </div>
    );
  }
  
  // Rich Text Element UI
  if (isRichText) {
    return (
      <div 
        className="rich-text-editor-popup" 
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? 'grabbing' : 'auto',
          zIndex: 9999
        }}
        onClick={e => e.stopPropagation()}
        ref={popupRef}
        data-element-type="rich-text-editor"
      >
        <RichTextEditor
          value={popupSettings.text}
          onChange={handleRichTextChange}
          onBlur={onClose}
          autoFocus={true}
          renderInMainContainer={true}
        />
      </div>
    );
  }
  
  // Default empty popup for other element types
  return (
    <div 
      className="element-settings-popup" 
      style={popupStyle}
      onClick={e => e.stopPropagation()}
      ref={popupRef}
    >
      <div 
        className="popup-header-settings popup-header-drag"
        onMouseDown={handleMouseDown}
      >
        <h4>{element.type.toUpperCase()} Settings</h4>
        <button 
          className="popup-close-btn" 
          onClick={onClose}
          title="Close"
        >
          ×
        </button>
      </div>
      
      <p>Quick settings not available for this element type.</p>
      
      <div className="popup-actions">
        <button 
          className="show-all-settings" 
          onClick={handleShowAllSettings}
        >
          Show All Settings
        </button>
      </div>
    </div>
  );
}

export default ElementSettingsPopup; 